/**
 * PipelineHealthBanner — Phase G · discrete admin-header telemetry
 *
 * Three compact, color-coded mini-badges that surface the current
 * routing of each media pipeline (Modal serverless vs. local pod):
 *
 *   ⚡ Modal T4    (emerald)   — pipeline pushed to Modal GPU
 *   ⚡ Modal CPU   (emerald)   — pipeline pushed to Modal CPU
 *   💾 Local       (amber)     — pipeline still running in-pod
 *
 * Hover reveals a 24-h job-history breakdown so the admin can see
 * whether the Modal-vs-local mix is healthy at a glance.
 *
 * Designed to live in the sidebar footer above the user-profile block
 * (see Sidebar.jsx). Discrete enough to not steal attention but always
 * visible — the user explicitly asked for "permanent but discrete".
 */
import React, { useState } from "react";
import { Zap, HardDrive, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import usePipelineStats from "@/hooks/usePipelineStats";
import useAnomalyToasts from "@/hooks/useAnomalyToasts";
import { testModalConnection } from "@/lib/api";

// Map each pipeline to its short label + Modal-flavour string so the
// badge can read either "Modal T4" or "Modal CPU" depending on which
// flag flipped.
const MODAL_FLAVOUR = {
  render: "T4",   // GPU video re-render
  harvest: "CPU", // Pass-vector slicing
  frames: "CPU",  // UI frame extractor
};
const SHORT_LABEL = {
  render: "Render",
  harvest: "Harvest",
  frames: "Frames",
};

function Badge({ pipelineKey, info }) {
  const isModal = info?.executor === "modal";
  const flavour = MODAL_FLAVOUR[pipelineKey] || "Modal";
  return (
    <div
      data-testid={`pipeline-badge-${pipelineKey}`}
      data-executor={info?.executor || "unknown"}
      className={[
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm",
        "font-mono text-[9px] uppercase tracking-wider border",
        isModal
          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
          : "bg-amber-500/10 border-amber-500/30 text-amber-400",
      ].join(" ")}
      title={`${info?.label || pipelineKey} → ${info?.executor || "?"}`}
    >
      {isModal ? (
        <Zap className="w-2.5 h-2.5" strokeWidth={2.5} />
      ) : (
        <HardDrive className="w-2.5 h-2.5" strokeWidth={2.5} />
      )}
      <span>{SHORT_LABEL[pipelineKey] || pipelineKey}</span>
      <span className="opacity-60">{isModal ? flavour : "Local"}</span>
    </div>
  );
}

function HistoryLine({ kind, stats }) {
  const done = stats?.done || 0;
  const failed = stats?.failed || 0;
  const running = stats?.running || 0;
  const total = done + failed + running + (stats?.queued || 0);
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-[10px] font-mono">
      <span className="text-muted-foreground truncate">{kind}</span>
      <span className="flex items-center gap-1.5 shrink-0">
        <span className="text-emerald-400">{done}✓</span>
        {failed > 0 && <span className="text-rose-400">{failed}✗</span>}
        {running > 0 && <span className="text-sky-400">{running}…</span>}
      </span>
    </div>
  );
}

/**
 * RecoveryCTA — actionable "Re-Verify Modal Credentials" button.
 *
 * Renders ONLY when the active anomaly set includes `credentials_missing`
 * — the only anomaly with a programmatic recovery path right now
 * (`failures_exceed` requires manual log inspection). Clicking calls the
 * backend's `POST /admin/modal/test-connection` preflight and toasts the
 * result. If the call returns `ok: true`, the next pipeline-stats poll
 * (≤ 30 s later) clears the anomaly and the sidebar dot disappears via
 * the rising/falling-edge `useAnomalyToasts` hook.
 */
function RecoveryCTA({ anomalyReasons }) {
  const [busy, setBusy] = useState(false);
  const hasCredsAnomaly = (anomalyReasons || []).some(
    (r) => r.code === "credentials_missing",
  );
  if (!hasCredsAnomaly) return null;

  const handleClick = async (e) => {
    // Tooltip closes on click outside its content — stop propagation so a
    // stray click doesn't dismiss the tooltip mid-action.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const res = await testModalConnection();
      if (res?.ok) {
        toast.success("Modal connection verified", {
          description: res.message || "Credentials are active.",
          duration: 6_000,
          id: "modal-recovery-ok",
        });
      } else {
        toast.error("Modal still unreachable", {
          description: res?.message || `code=${res?.code || "unknown"}`,
          duration: 10_000,
          id: "modal-recovery-fail",
        });
      }
    } catch (err) {
      toast.error("Modal connection test failed", {
        description: err?.message || "Network error",
        duration: 8_000,
        id: "modal-recovery-error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid="modal-recovery-cta"
      onClick={handleClick}
      disabled={busy}
      className={[
        "mt-2 w-full inline-flex items-center justify-center gap-1.5",
        "px-2 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-wider",
        "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
        "hover:bg-emerald-500/20 hover:border-emerald-500/60",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "transition-colors",
      ].join(" ")}
    >
      {busy ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <ShieldCheck className="w-3 h-3" />
      )}
      <span>{busy ? "Verifying…" : "Re-verify Modal credentials"}</span>
    </button>
  );
}


function Tooltip24h({ data, error, anomalyReasons }) {
  const recent = data?.recent_jobs;
  const byKind = recent?.by_kind || {};
  const byExecutor = recent?.by_executor || {};
  const hasCreds = data?.modal?.has_credentials;
  const hasAnomaly = (anomalyReasons || []).length > 0;
  return (
    <div
      data-testid="pipeline-health-tooltip"
      className="w-64 space-y-2 text-foreground"
    >
      <div className="font-display uppercase tracking-widest text-[10px] text-primary">
        Pipeline Health · 24h
      </div>

      {/* Anomaly warning — surfaces FIRST so the admin sees it before any stats */}
      {hasAnomaly && (
        <div
          data-testid="pipeline-anomaly-warning"
          className="space-y-1 rounded-sm border border-rose-500/40 bg-rose-500/10 px-2 py-1.5"
        >
          {anomalyReasons.map((r) => (
            <div
              key={r.code}
              data-testid={`anomaly-reason-${r.code}`}
              className="flex items-start gap-1.5 text-[10px] font-mono text-rose-300"
            >
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2.5} />
              <span className="leading-tight">⚠️ Warning: {r.message}</span>
            </div>
          ))}
          <RecoveryCTA anomalyReasons={anomalyReasons} />
        </div>
      )}

      <div className="space-y-0.5">
        <div className="flex items-center justify-between text-[10px] font-mono">
          <span className="text-muted-foreground">Modal calls</span>
          <span className="text-emerald-400">{byExecutor.modal || 0}</span>
        </div>
        <div className="flex items-center justify-between text-[10px] font-mono">
          <span className="text-muted-foreground">Local calls</span>
          <span className="text-amber-400">{byExecutor.local || 0}</span>
        </div>
      </div>

      <div className="h-px bg-border" />

      <div className="space-y-0.5">
        {Object.keys(byKind).length === 0 ? (
          <div className="text-[10px] font-mono text-muted-foreground italic">
            No jobs in the last 24h
          </div>
        ) : (
          Object.entries(byKind).map(([kind, stats]) => (
            <HistoryLine key={kind} kind={kind} stats={stats} />
          ))
        )}
      </div>

      <div className="h-px bg-border" />

      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className="text-muted-foreground">Modal credentials</span>
        <span className={hasCreds ? "text-emerald-400" : "text-rose-400"}>
          {hasCreds ? "configured" : "missing"}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-rose-400 pt-1 border-t border-border">
          <AlertCircle className="w-3 h-3" />
          <span className="truncate">poll error: {error}</span>
        </div>
      )}
    </div>
  );
}

export default function PipelineHealthBanner() {
  const { data, error, loading, anomalyReasons } = usePipelineStats();

  // Rising/falling-edge toast notifications on state degradation +
  // recovery. Hook is invoked exactly once because this component is
  // mounted exactly once (in the Sidebar) — no risk of duplicates.
  useAnomalyToasts(anomalyReasons);

  // First-paint placeholder: render three muted badges so the banner's
  // FOOTPRINT is stable from the very first frame — no layout shift when
  // the first poll resolves.
  const pipelines =
    data?.pipelines || {
      render: { executor: "unknown", label: "Render" },
      harvest: { executor: "unknown", label: "Harvest" },
      frames: { executor: "unknown", label: "Frames" },
    };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid="pipeline-health-banner"
            data-loading={loading ? "true" : "false"}
            className="flex items-center gap-1 cursor-help select-none"
          >
            <Badge pipelineKey="render" info={pipelines.render} />
            <Badge pipelineKey="harvest" info={pipelines.harvest} />
            <Badge pipelineKey="frames" info={pipelines.frames} />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="bg-background border border-border p-3 shadow-lg"
        >
          <Tooltip24h data={data} error={error} anomalyReasons={anomalyReasons} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
